/**
 * Weather Layers
 * Unified factory for creating weather raster layers
 */

import type { Map as MapboxMap } from 'mapbox-gl';
import { nowTimestampSeconds } from '@/utils/date';
import { WindParticleLayer } from '../wind/WindParticleLayer';
import { waitForStyleLoaded } from '../geoJsonLayerHelper';
import { createRasterLayer, createRasterSource, type RasterLayerConfig, removeLayerAndSource } from './layerBuilder';

const WEATHER_API_KEY = import.meta.env.VITE_WEATHER_API_KEY || '8364000e0a00de7c2595d7105755b855';
const WEATHER_BASE_URL = 'https://maps.openweathermap.org/maps/2.0/weather';

/**
 * Weather layer types
 */
export type WeatherLayerType = 'Clouds' | 'Temperature' | 'Pressure' | 'Wind';

/**
 * Weather layer configuration mapping
 */
const WEATHER_CONFIG: Record<WeatherLayerType, { endpoint: string; opacity: string }> = {
  Clouds: { endpoint: 'CL', opacity: '0.5' },
  Temperature: { endpoint: 'TA2', opacity: '0.4' },
  Pressure: { endpoint: 'APM', opacity: '0.25' },
  Wind: { endpoint: 'WND', opacity: '0.25' },
};

/**
 * Create a weather layer
 * @param map - Mapbox map instance
 * @param type - Weather layer type
 */
export async function createWeatherLayer(map: MapboxMap | null, type: WeatherLayerType): Promise<void> {
  if (!map) return;

  // Wait for style to be loaded
  const styleReady = await waitForStyleLoaded(map);
  if (!styleReady) {
    console.warn(`Weather [${type}]: style not ready, skipping`);
    return;
  }

  const config = WEATHER_CONFIG[type];
  const sourceId = `Weather - ${type} Source`;
  const layerId = `Weather - ${type} Layer`;

  // Remove existing layer and source
  removeLayerAndSource(map, layerId, sourceId);

  // Create tile URL with current timestamp
  const timestamp = nowTimestampSeconds();
  const tiles = [
    `${WEATHER_BASE_URL}/${config.endpoint}/{z}/{x}/{y}?date=${timestamp}&opacity=${config.opacity}&fill_bound=true&appid=${WEATHER_API_KEY}`,
  ];

  // Create source configuration
  const sourceConfig: RasterLayerConfig = {
    layerId,
    sourceId,
    tiles,
    tileSize: 256,
  };

  // Remove existing source if it exists
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }

  // Add source
  map.addSource(sourceId, createRasterSource(sourceConfig));

  // Remove existing layer if it exists
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }

  // Create and add layer
  const layerConfig = createRasterLayer({
    ...sourceConfig,
    visibility: 'none',
  });

  map.addLayer(layerConfig);
}

/**
 * Add Clouds layer
 */
export async function addClouds(map: MapboxMap | null): Promise<void> {
  await createWeatherLayer(map, 'Clouds');
}

/**
 * Add Temperature layer
 */
export async function addTemperature(map: MapboxMap | null): Promise<void> {
  await createWeatherLayer(map, 'Temperature');
}

/**
 * Add Atmospheric Pressure layer
 */
export async function addPressure(map: MapboxMap | null): Promise<void> {
  await createWeatherLayer(map, 'Pressure');
}

/** Singleton wind particle layer instance (survives add/remove cycles) */
let windLayerInstance: WindParticleLayer | null = null;

/**
 * Add Wind layer — custom WebGL particle animation (Windy-style)
 * Uses GPU-accelerated particles with bilinear interpolation for smooth rendering at any zoom.
 * Data sources: HRRR 3km (North America) + GFS 28km (global) via S3/CloudFront.
 */
export async function addWind(map: MapboxMap | null): Promise<void> {
  if (!map) return;

  const styleReady = await waitForStyleLoaded(map);
  if (!styleReady) {
    console.warn('Weather [Wind]: style not ready, skipping');
    return;
  }

  const layerId = 'Weather - Wind Layer';

  // Remove existing layer if present (handles style reloads)
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
    windLayerInstance = null;
  }

  // Create and add the custom WebGL particle layer
  windLayerInstance = new WindParticleLayer(layerId);
  map.addLayer(windLayerInstance as mapboxgl.CustomLayerInterface);

  // Start hidden (matches other weather sublayers) — user toggles visibility via panel
  map.setLayoutProperty(layerId, 'visibility', 'none');
}
